import { Skeleton } from "@/components/ui/skeleton";
import { ChartCard } from "./chart-card";
import { cn } from "@/lib/utils";
import type { MajorQuotaRow } from "@/lib/statistics-api.service";

interface QuotaFillBulletChartProps {
	/** `undefined` = lần tải đầu chưa xong. */
	rows: MajorQuotaRow[] | undefined;
}

/**
 * Sắp ngành theo `fillRate` TĂNG DẦN — ngành lấp đầy thấp nhất lên đầu, để
 * người xem thấy ngay ngành nào còn xa chỉ tiêu mà không phải dò cả danh sách.
 *
 * Ngành `quota = 0` (chưa có chỉ tiêu, `fillRate` không có ý nghĩa) đẩy xuống
 * CUỐI thay vì tham gia sắp xếp — chúng không có gì để so `fillRate`, và
 * `fillRate = 0` từ server sẽ khiến chúng lẫn lộn với ngành có chỉ tiêu nhưng
 * chưa nhận sinh viên nào (cũng `fillRate = 0`, nhưng có `quota > 0`).
 *
 * KHÔNG gắn cờ/màu "nguy cơ" cho ngành lấp đầy thấp: chỉ xếp hạng theo thứ tự,
 * không phán xét — người dùng đã quyết không đặt ngưỡng cảnh báo.
 */
export function sortQuotaRows(rows: readonly MajorQuotaRow[]): MajorQuotaRow[] {
	const withQuota = rows.filter((r) => r.quota > 0).sort((a, b) => a.fillRate - b.fillRate);
	const withoutQuota = rows.filter((r) => r.quota <= 0);
	return [...withQuota, ...withoutQuota];
}

/** Nhãn ngành, kèm mã khi tên trùng với một ngành khác trong cùng danh sách. */
function labelFor(row: MajorQuotaRow, duplicateNames: Set<string>): string {
	if (!row.majorName) return row.majorCode || "Ngành không xác định";
	return duplicateNames.has(row.majorName) ? `${row.majorName} (${row.majorCode})` : row.majorName;
}

export function QuotaFillBulletChart({ rows }: QuotaFillBulletChartProps) {
	if (rows === undefined) {
		return (
			<ChartCard title="Tỉ lệ lấp đầy chỉ tiêu theo ngành" filename="ti-le-lap-day-chi-tieu-nganh">
				<div className="space-y-3">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-6 w-full" />
					))}
				</div>
			</ChartCard>
		);
	}

	if (rows.length === 0) {
		return (
			<ChartCard title="Tỉ lệ lấp đầy chỉ tiêu theo ngành" filename="ti-le-lap-day-chi-tieu-nganh">
				<p className="py-8 text-center text-sm text-muted-foreground">
					Chưa có dữ liệu chỉ tiêu ngành trong phạm vi đang lọc.
				</p>
			</ChartCard>
		);
	}

	const sorted = sortQuotaRows(rows);
	const nameCounts = new Map<string, number>();
	for (const row of rows) {
		if (row.majorName) nameCounts.set(row.majorName, (nameCounts.get(row.majorName) ?? 0) + 1);
	}
	const duplicateNames = new Set(
		[...nameCounts.entries()].filter(([, count]) => count > 1).map(([name]) => name),
	);

	return (
		<ChartCard
			title="Tỉ lệ lấp đầy chỉ tiêu theo ngành"
			filename="ti-le-lap-day-chi-tieu-nganh"
			description="Sắp theo tỉ lệ lấp đầy tăng dần — thanh đậm là đã tiếp nhận, thanh mờ là đã trúng tuyển, vạch đứng là chỉ tiêu"
		>
			<div className="space-y-2.5">
				{sorted.map((row) => {
					// Mỗi hàng tự co giãn theo giá trị lớn nhất CỦA CHÍNH NÓ (chỉ tiêu,
					// trúng tuyển, tiếp nhận): 39 ngành có chỉ tiêu chênh lệch rất lớn
					// (vài chục tới vài trăm), một thang chung sẽ làm ngành nhỏ co lại
					// thành vệt kẻ không đọc được trong khi tỉ lệ % cạnh tên đã đủ để
					// so sánh giữa các ngành với nhau.
					const scale = Math.max(row.quota, row.admitted, row.received, 1);
					const admittedPct = Math.min(100, (row.admitted / scale) * 100);
					const receivedPct = Math.min(100, (row.received / scale) * 100);
					const quotaPct = row.quota > 0 ? Math.min(100, (row.quota / scale) * 100) : null;

					return (
						<div key={row.majorCode} className="flex items-center gap-3">
							<span
								className="w-40 flex-shrink-0 truncate text-sm md:w-56"
								title={row.majorName || row.majorCode}
							>
								{labelFor(row, duplicateNames)}
							</span>
							<div className="relative h-4 flex-1 rounded bg-secondary">
								<div
									className="absolute inset-y-0 left-0 rounded bg-primary/30"
									style={{ width: `${admittedPct}%` }}
									title={`Trúng tuyển: ${row.admitted.toLocaleString("vi-VN")}`}
								/>
								<div
									className="absolute inset-y-0 left-0 rounded bg-primary"
									style={{ width: `${receivedPct}%` }}
									title={`Đã tiếp nhận: ${row.received.toLocaleString("vi-VN")}`}
								/>
								{quotaPct !== null && (
									<div
										className="absolute inset-y-0 w-0.5 bg-foreground"
										style={{ left: `${quotaPct}%` }}
										title={`Chỉ tiêu: ${row.quota.toLocaleString("vi-VN")}`}
									/>
								)}
							</div>
							<span
								className={cn(
									"w-28 flex-shrink-0 text-right text-sm font-semibold",
									row.quota <= 0 && "font-normal text-muted-foreground",
								)}
							>
								{row.quota > 0 ? `${row.fillRate.toFixed(1)}%` : "chưa có chỉ tiêu"}
							</span>
						</div>
					);
				})}
			</div>
		</ChartCard>
	);
}
